import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useRef } from 'react';
import { useFormikContext } from 'formik';
import Field from '../Field.js';
import FieldGroup, { FieldChoiceGroup, FieldHint, FieldRow } from '../FieldGroup.js';
import FormGroup from '@helios/FormGroup';
import TextField from '@helios/TextField';
import countries from '@/utils/countries.json';
import states from '@/utils/states.json';
import { HDYHAU_OPTIONS, getHdyhauFollowUp, getNestedTextOption } from './hdyhauConfig.js';
import AddressAutocompleteField from './AddressAutocompleteField.js';
import ProgramSelect from './ProgramSelect.js';
const countryKeys = Object.keys(countries).sort((a, b) => a === 'US' ? -1 : b === 'US' ? 1 : 0);
const stateKeys = Object.keys(states).sort((a, b) => 0);
function HdyhauFollowUpFields(t0) {
  const $ = _c(25);
  const {
    followUp,
    employerDisabled
  } = t0;
  const formik = useFormikContext();
  const {
    hearAboutCampusFollowUp
  } = formik.values;
  if (!followUp || followUp.field === "otherDescription" && employerDisabled) {
    return null;
  }
  if (followUp.type === "text") {
    const fieldName = followUp.field === "otherDescription" ? "otherDescription" : "hearAboutCampusFollowUpDetail";
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FieldHint>(optional)</FieldHint>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== followUp.question) {
      t2 = <>{followUp.question} {t1}</>;
      $[1] = followUp.question;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== fieldName || $[4] !== followUp.placeholder) {
      t3 = <Field id={fieldName} name={fieldName} placeholder={followUp.placeholder} />;
      $[3] = fieldName;
      $[4] = followUp.placeholder;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t2 || $[7] !== t3) {
      t4 = <FieldGroup label={t2}>{t3}</FieldGroup>;
      $[6] = t2;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t1;
  if ($[9] !== followUp || $[10] !== hearAboutCampusFollowUp) {
    t1 = getNestedTextOption(followUp, hearAboutCampusFollowUp);
    $[9] = followUp;
    $[10] = hearAboutCampusFollowUp;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const nestedOption = t1;
  let t2;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FieldHint>(optional)</FieldHint>;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== followUp.question) {
    t3 = <span id="hdyhau-followup-label">{followUp.question} {t2}</span>;
    $[13] = followUp.question;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== followUp.options) {
    t4 = followUp.options.map(_temp);
    $[15] = followUp.options;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t3 || $[18] !== t4) {
    t5 = <FieldChoiceGroup legend={t3} name="hearAboutCampusFollowUp">{t4}</FieldChoiceGroup>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== nestedOption) {
    t6 = nestedOption && <FieldGroup><Field name="hearAboutCampusFollowUpDetail" placeholder={nestedOption.nestedText.placeholder} aria-label={nestedOption.nestedText.placeholder} /></FieldGroup>;
    $[20] = nestedOption;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t5 || $[23] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function _temp(option) {
  return <Field key={option.value} type="radio" name="hearAboutCampusFollowUp" value={option.value} label={option.label} />;
}
function PersonalInformation(t0) {
  const $ = _c(88);
  const {
    autoFocus: t1,
    email,
    degrees: t2,
    passwordDisabled: t3,
    employerDisabled: t4,
    referralCodeDisabled: t5,
    showProgram: t6
  } = t0;
  const autoFocus = t1 === undefined ? true : t1;
  let t7;
  if ($[0] !== t2) {
    t7 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const degrees = t7;
  const passwordDisabled = t3 === undefined ? false : t3;
  const employerDisabled = t4 === undefined ? false : t4;
  const referralCodeDisabled = t5 === undefined ? false : t5;
  const showProgram = t6 === undefined ? true : t6;
  const formik = useFormikContext();
  const {
    hearAboutCampus
  } = formik.values;
  let t8;
  if ($[2] !== hearAboutCampus) {
    t8 = getHdyhauFollowUp(hearAboutCampus);
    $[2] = hearAboutCampus;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const followUp = t8;
  const prevHearAboutCampus = useRef(hearAboutCampus);
  let t9;
  if ($[4] !== formik || $[5] !== hearAboutCampus) {
    t9 = () => {
      if (prevHearAboutCampus.current === hearAboutCampus) {
        return;
      }
      prevHearAboutCampus.current = hearAboutCampus;
      if (formik.values.hearAboutCampusFollowUp) {
        formik.setFieldValue("hearAboutCampusFollowUp", "");
      }
      if (formik.values.hearAboutCampusFollowUpDetail) {
        formik.setFieldValue("hearAboutCampusFollowUpDetail", "");
      }
      if (formik.values.otherDescription) {
        formik.setFieldValue("otherDescription", "");
      }
      if (formik.values.referredBy && formik.values.hearAboutCampus !== "referred") {
        formik.setFieldValue("referredBy", "");
      }
    };
    $[4] = formik;
    $[5] = hearAboutCampus;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  let t10;
  if ($[7] !== hearAboutCampus) {
    t10 = [hearAboutCampus];
    $[7] = hearAboutCampus;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  useEffect(t9, t10);
  let t11;
  if ($[9] !== followUp || $[10] !== formik) {
    t11 = () => {
      const nestedOption = getNestedTextOption(followUp, formik.values.hearAboutCampusFollowUp);
      if (!nestedOption && formik.values.hearAboutCampusFollowUpDetail) {
        formik.setFieldValue("hearAboutCampusFollowUpDetail", "");
      }
    };
    $[9] = followUp;
    $[10] = formik;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== followUp || $[13] !== formik.values.hearAboutCampusFollowUp) {
    t12 = [formik.values.hearAboutCampusFollowUp, followUp];
    $[12] = followUp;
    $[13] = formik.values.hearAboutCampusFollowUp;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  useEffect(t11, t12);
  let t13;
  if ($[15] !== autoFocus) {
    t13 = <FieldGroup label="First name"><Field name="firstName" autoFocus={autoFocus} /></FieldGroup>;
    $[15] = autoFocus;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <FieldGroup label="Last name"><Field name="lastName" /></FieldGroup>;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t13) {
    t15 = <FieldRow>{t13}{t14}</FieldRow>;
    $[18] = t13;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <FieldGroup label={<>Middle name <FieldHint>(optional)</FieldHint></>}><Field name="middleName" /></FieldGroup>;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <FieldGroup label={<>Preferred name <FieldHint>(optional)</FieldHint></>}><Field name="preferredName" /></FieldGroup>;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <FieldGroup label="Street Address 1" name="address"><AddressAutocompleteField id="address" /></FieldGroup>;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <FieldGroup label={<>Street Address 2<FieldHint>(optional)</FieldHint></>}><Field name="secondAddress" autoComplete="address-line2" /></FieldGroup>;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <FieldGroup label="Country"><Field as="select" name="country" autoComplete="country">{countryKeys.map(_temp2)}</Field></FieldGroup>;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <FieldGroup label="City"><Field name="city" autoComplete="address-level2" /></FieldGroup>;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  const t22 = formik.values.country === "US" ? "State" : "State / Province";
  const t23 = !!formik.touched.state && !!formik.errors.state ? formik.values.country === "US" ? "Choose your state." : "Required." : undefined;
  let t24;
  if ($[26] !== formik.values.country) {
    t24 = formik.values.country === "US" ? <Field as="select" name="state" autoComplete="address-level1">{stateKeys.map(_temp3)}</Field> : <Field name="state" autoComplete="address-level1" />;
    $[26] = formik.values.country;
    $[27] = t24;
  } else {
    t24 = $[27];
  }
  let t25;
  if ($[28] !== t22 || $[29] !== t23 || $[30] !== t24) {
    t25 = <FieldGroup label={t22} error={t23}>{t24}</FieldGroup>;
    $[28] = t22;
    $[29] = t23;
    $[30] = t24;
    $[31] = t25;
  } else {
    t25 = $[31];
  }
  const t26 = formik.values.country === "US" && !!formik.touched.zip && !!formik.errors.zip ? "Incorrect format." : undefined;
  let t27;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Field name="zip" autoComplete="postal-code" />;
    $[32] = t27;
  } else {
    t27 = $[32];
  }
  let t28;
  if ($[33] !== t26) {
    t28 = <FieldGroup label="Zip" error={t26}>{t27}</FieldGroup>;
    $[33] = t26;
    $[34] = t28;
  } else {
    t28 = $[34];
  }
  let t29;
  if ($[35] !== t25 || $[36] !== t28) {
    t29 = <FieldRow split="wide-narrow">{t25}{t28}</FieldRow>;
    $[35] = t25;
    $[36] = t28;
    $[37] = t29;
  } else {
    t29 = $[37];
  }
  const t30 = formik.values.country || "US";
  let t31;
  if ($[38] !== t30) {
    t31 = <FieldGroup label="Mobile Phone"><Field name="mobilePhone" formatOptions={{
        phone: true,
        phoneRegionCode: t30
      }} showCountrySelect={true} /></FieldGroup>;
    $[38] = t30;
    $[39] = t31;
  } else {
    t31 = $[39];
  }
  let t32;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <>Home Phone<FieldHint>(optional)</FieldHint></>;
    $[40] = t32;
  } else {
    t32 = $[40];
  }
  const t33 = formik.values.country || "US";
  let t34;
  if ($[41] !== t33) {
    t34 = <FieldGroup label={t32}><Field name="homePhone" formatOptions={{
        phone: true,
        phoneRegionCode: t33
      }} showCountrySelect={true} /></FieldGroup>;
    $[41] = t33;
    $[42] = t34;
  } else {
    t34 = $[42];
  }
  let t35;
  if ($[43] !== employerDisabled) {
    t35 = employerDisabled ? <ReadOnlyField label="How did you hear about Campus?" value="My Employer" /> : <FieldGroup label="How did you hear about Campus?"><Field as="select" name="hearAboutCampus">{HDYHAU_OPTIONS.map(_temp4)}</Field></FieldGroup>;
    $[43] = employerDisabled;
    $[44] = t35;
  } else {
    t35 = $[44];
  }
  let t36;
  if ($[45] !== formik.values.hearAboutCampus || $[46] !== formik.values.referredBy || $[47] !== referralCodeDisabled) {
    t36 = formik.values.hearAboutCampus === "referred" && (referralCodeDisabled ? <ReadOnlyField label="Enter name of student or referral code" value={formik.values.referredBy} /> : <FieldGroup label="Enter name of student or referral code"><Field name="referredBy" /></FieldGroup>);
    $[45] = formik.values.hearAboutCampus;
    $[46] = formik.values.referredBy;
    $[47] = referralCodeDisabled;
    $[48] = t36;
  } else {
    t36 = $[48];
  }
  let t37;
  if ($[49] !== employerDisabled || $[50] !== formik.values.hearAboutCampus) {
    t37 = formik.values.hearAboutCampus === "myEmployer" && employerDisabled && <ReadOnlyField label="What is the name of your employer?" value="Forever 21" />;
    $[49] = employerDisabled;
    $[50] = formik.values.hearAboutCampus;
    $[51] = t37;
  } else {
    t37 = $[51];
  }
  let t38;
  if ($[52] !== employerDisabled || $[53] !== followUp || $[54] !== formik.values.hearAboutCampus) {
    t38 = formik.values.hearAboutCampus === "myEmployer" && !employerDisabled && followUp && <HdyhauFollowUpFields followUp={followUp} />;
    $[52] = employerDisabled;
    $[53] = followUp;
    $[54] = formik.values.hearAboutCampus;
    $[55] = t38;
  } else {
    t38 = $[55];
  }
  let t39;
  if ($[56] !== employerDisabled || $[57] !== followUp || $[58] !== formik.values.hearAboutCampus) {
    t39 = formik.values.hearAboutCampus !== "myEmployer" && formik.values.hearAboutCampus !== "referred" && followUp && <HdyhauFollowUpFields followUp={followUp} employerDisabled={employerDisabled} />;
    $[56] = employerDisabled;
    $[57] = followUp;
    $[58] = formik.values.hearAboutCampus;
    $[59] = t39;
  } else {
    t39 = $[59];
  }
  let t40;
  if ($[60] !== degrees || $[61] !== showProgram) {
    t40 = showProgram && <ProgramSelect degrees={degrees} label="Which program are you interested in applying for?" />;
    $[60] = degrees;
    $[61] = showProgram;
    $[62] = t40;
  } else {
    t40 = $[62];
  }
  let t41;
  if ($[63] !== email) {
    t41 = <ReadOnlyField label="Email" type="email" value={email} />;
    $[63] = email;
    $[64] = t41;
  } else {
    t41 = $[64];
  }
  const t42 = passwordDisabled ? "********" : undefined;
  let t43;
  if ($[65] !== passwordDisabled || $[66] !== t42) {
    t43 = <FieldGroup label="Password"><Field name="password" type="password" disabled={passwordDisabled} placeholder={t42} /></FieldGroup>;
    $[65] = passwordDisabled;
    $[66] = t42;
    $[67] = t43;
  } else {
    t43 = $[67];
  }
  const t44 = formik.errors.passwordConfirm ? "Password must match above." : undefined;
  const t45 = passwordDisabled ? "********" : undefined;
  let t46;
  if ($[68] !== passwordDisabled || $[69] !== t45) {
    t46 = <Field name="passwordConfirm" type="password" disabled={passwordDisabled} placeholder={t45} />;
    $[68] = passwordDisabled;
    $[69] = t45;
    $[70] = t46;
  } else {
    t46 = $[70];
  }
  let t47;
  if ($[71] !== t44 || $[72] !== t46) {
    t47 = <FieldGroup label="Confirm password" error={t44}>{t46}</FieldGroup>;
    $[71] = t44;
    $[72] = t46;
    $[73] = t47;
  } else {
    t47 = $[73];
  }
  let t48;
  if ($[74] !== t15 || $[75] !== t29 || $[76] !== t31 || $[77] !== t34 || $[78] !== t35 || $[79] !== t36 || $[80] !== t37 || $[81] !== t38 || $[82] !== t39 || $[83] !== t40 || $[84] !== t41 || $[85] !== t43 || $[86] !== t47) {
    t48 = <>{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t29}{t31}{t34}{t35}{t36}{t37}{t38}{t39}{t40}{t41}{t43}{t47}</>;
    $[74] = t15;
    $[75] = t29;
    $[76] = t31;
    $[77] = t34;
    $[78] = t35;
    $[79] = t36;
    $[80] = t37;
    $[81] = t38;
    $[82] = t39;
    $[83] = t40;
    $[84] = t41;
    $[85] = t43;
    $[86] = t47;
    $[87] = t48;
  } else {
    t48 = $[87];
  }
  return t48;
}

/**
 * A disabled, read-only value with no Formik field (the signed-in email, an
 * employer's preset answers): a TextField in a FieldGroup-free FormGroup.
 */
function _temp4(t0) {
  const {
    value,
    label
  } = t0;
  return <option key={value} value={value}>{label}</option>;
}
function _temp3(k_0) {
  return <option key={k_0} value={k_0}>{states[k_0]}</option>;
}
function _temp2(k) {
  return <option key={k} value={k}>{countries[k]}</option>;
}
function ReadOnlyField(t0) {
  const $ = _c(8);
  const {
    label,
    value,
    type
  } = t0;
  const id = useId();
  const t1 = value ?? "";
  let t2;
  if ($[0] !== id || $[1] !== t1 || $[2] !== type) {
    t2 = <TextField id={id} type={type} disabled={true} readOnly={true} value={t1} />;
    $[0] = id;
    $[1] = t1;
    $[2] = type;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== id || $[5] !== label || $[6] !== t2) {
    t3 = <FormGroup label={label} htmlFor={id}>{t2}</FormGroup>;
    $[4] = id;
    $[5] = label;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export default PersonalInformation;